/** Metric value formatting by semantic unit. */

import type { MetricUnit } from '@lognai/plugin-analytics';

const compactCurrency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

const integer = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

/** Format a metric value for display given its unit. */
export function formatMetric(value: number, unit: MetricUnit | 'number', currency?: string): string {
  switch (unit) {
    case 'currency':
      return currency && currency !== 'USD'
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency, notation: 'compact', maximumFractionDigits: 1 }).format(value)
        : compactCurrency.format(value);
    case 'percent':
      return `${(value * 100).toFixed(1)}%`;
    case 'days':
      return `${value.toFixed(1)} d`;
    case 'ratio':
      return value.toFixed(2);
    case 'count':
    case 'number':
    default:
      return integer.format(value);
  }
}

/** Signed percentage change, e.g. "+8.0%". */
export function formatDeltaPct(deltaPct: number | null): string {
  if (deltaPct == null) return '—';
  return `${deltaPct >= 0 ? '+' : ''}${(deltaPct * 100).toFixed(1)}%`;
}

export type Tone = 'neutral' | 'success' | 'warning' | 'destructive' | 'info';

/** Tone for a delta given whether the move is an improvement. */
export function deltaTone(improving: boolean, hasChange: boolean): Tone {
  if (!hasChange) return 'neutral';
  return improving ? 'success' : 'destructive';
}

/** Tone for a health band. */
export function bandTone(band: string): Tone {
  switch (band) {
    case 'strong':
      return 'success';
    case 'healthy':
      return 'info';
    case 'at_risk':
      return 'warning';
    case 'critical':
      return 'destructive';
    default:
      return 'neutral';
  }
}

/** Direction arrow for a delta. */
export function deltaDirection(delta: number): 'up' | 'down' | 'flat' {
  if (delta > 0) return 'up';
  if (delta < 0) return 'down';
  return 'flat';
}
